import React, { useState } from "react";
import { Table, message } from "antd";
import * as XLSX from "xlsx";
import { BULK_UPLOAD_QUIZ_OPTIONS, QUIZ_TEMPLATE_FILE } from "../../../../assets/FormFeilds/Config/config";
import CustomButton from "../../../../components/shared/button/CustomButton";
import CustomUploadField from "../../../../components/shared/new Form/Fields/CustomUploadFile";
import CustomModal from "../../../../components/shared/CustomModal/CustomModal";

// Sample data for template preview including Row Number / S. No.
const dataSource = [
  {
    key: 1,
    sNo: 1,
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@nucleusteq.com",
    employeeNumber: "EMP001",
    role: "EMPLOYEE",
  },
  {
    key: 2,
    sNo: 2,
    firstName: "Alice",
    lastName: "Johnson",
    email: "alice.johnson@nucleusteq.com",
    employeeNumber: "EMP002",
    role: "MANAGER",
  },
];


const columns = [
  { title: "S. No.", dataIndex: "sNo", key: "sNo", width: 80 },
  { title: "First Name", dataIndex: "firstName", key: "firstName" },
  { title: "Last Name", dataIndex: "lastName", key: "lastName" },
  { title: "Email", dataIndex: "email", key: "email" },
  { title: "Employee Number", dataIndex: "employeeNumber", key: "employeeNumber" },
  { title: "Role", dataIndex: "role", key: "role" },
];

const UserBulkUploadModal = ({
  bulkUploadModalVisible,
  setBulkUploadModalVisible,
  onParsedDataSave,
}) => {
  const [downloadFormat, setDownloadFormat] = useState("csv");
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);

  const handleFormatSelect = (format) => {
    setDownloadFormat(format);
    setBulkUploadModalVisible(false);
    setIsDetailModalVisible(true);
  };

  const confirmDownloadTemplate = () => {
    if (!downloadFormat || !QUIZ_TEMPLATE_FILE?.[downloadFormat]) {
      message.error("Selected template format is not available.");
      return;
    }

    const link = document.createElement("a");
    link.href = QUIZ_TEMPLATE_FILE[downloadFormat];
    link.download = `user_bulk_upload_template.${downloadFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(`Template downloaded (${downloadFormat.toUpperCase()})`);
  };

  const handleFileChange = (info) => {
    let newFileList = [...info.fileList];
    setFileList(newFileList);

    const uploadedFile = info.file || (newFileList[0] && newFileList[0].originFileObj);

    if (uploadedFile) {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array", raw: true });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Map custom keys matching the user table layout
          const customKeys = [
            "firstName",
            "lastName",
            "email",
            "employeeNumber",
            "role",
          ];

          const rawJsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: customKeys,
            range: 1, // Skip header row
            raw: false,
            defval: "",
          });

          // Add rowNumber / sNo sequentially
          const formattedData = rawJsonData.map((item, index) => ({
            key: index + 1,
            rowNumber: index + 1,
            firstName: item.firstName || "",
            lastName: item.lastName || "",
            email: item.email || "",
            employeeNumber: item.employeeNumber || "",
            role: item.role || "",
          }));

          if (onParsedDataSave) {
            onParsedDataSave(formattedData);
          }

          setIsDetailModalVisible(false);
          message.success("File uploaded & parsed successfully!");
        } catch (error) {
          console.error("Error parsing file:", error);
          message.error("Failed to parse file content.");
        }
      };

      reader.readAsArrayBuffer(uploadedFile.originFileObj || uploadedFile);
    }
  };

  return (
    <>
      {/* Step 1: Format Selection Modal */}
      <CustomModal
        visible={bulkUploadModalVisible}
        onClose={() => setBulkUploadModalVisible(false)}
        width={600}
      >
        <div className="flex flex-col gap-[7px] p-4">
          <div className="flex gap-3 w-full">
            {BULK_UPLOAD_QUIZ_OPTIONS.map((option) => (
              <CustomButton
                key={option.value}
                onClick={() => handleFormatSelect(option.value)}
                className="flex-1 py-2 px-4 border-2 border-[#910E11] rounded-[10px] text-[#910E11] font-medium text-sm hover:bg-[#910E11] hover:text-white transition-colors cursor-pointer"
              >
                {option.label}
              </CustomButton>
            ))}
          </div>
          <span className="text-xs text-[rgba(70,70,70,0.6)]">
            Note: Only upload CSV, XLSX files for bulk users.
          </span>
        </div>
      </CustomModal>

      {/* Step 2: Details & Upload Modal */}
      <CustomModal
        title={`Bulk Upload Users (${downloadFormat.toUpperCase()})`}
        visible={isDetailModalVisible}
        width={800}
        onClose={() => setIsDetailModalVisible(false)}
      >
        <p className="text-[rgba(70,70,70,1)] text-sm mb-0">
          Please ensure you enter all the user details strictly in the format
          provided in the downloaded template. Incorrect formatting may lead to
          errors during bulk upload.
        </p>

        <Table
          size="small"
          pagination={false}
          bordered
          dataSource={dataSource}
          columns={columns}
          className="mt-4 mb-4"
        />

        <CustomUploadField
          accept={downloadFormat === "csv" ? ".csv" : ".xlsx,.xls"}
          fileList={fileList}
          handleFileChange={handleFileChange}
          disabled={false}
        />

        <div className="flex gap-2.5 mt-6 w-full">
          <CustomButton
            variant="primary"
            onClick={confirmDownloadTemplate}
            style={{ width: "50%" }}
          >
            Download {downloadFormat.toUpperCase()} Template
          </CustomButton>
          <CustomButton
            variant="outlined"
            onClick={() => setIsDetailModalVisible(false)}
            style={{ width: "50%" }}
          >
            Cancel
          </CustomButton>
        </div>
      </CustomModal>
    </>
  );
};

export default UserBulkUploadModal;